import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Optimizes image URLs with quality parameters and responsive sizing
 * @param url - The original image URL
 * @param options - Configuration for optimization
 */
export function optimizeImageUrl(url: string | null | undefined, options: {
  width?: number;
  height?: number;
  quality?: number;
  fit?: 'cover' | 'contain' | 'crop' | 'scale-down';
  format?: 'webp' | 'avif' | 'auto';
} = {}): string {
  if (!url) {
    return 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=320&q=75&auto=format&fm=webp&fit=crop';
  }

  // Never alter SVG vector files
  if (url.endsWith('.svg') || url.includes('.svg?')) {
    return url;
  }

  const { width = 320, height, quality = 80, fit = 'cover', format = 'webp' } = options;

  // Handle Unsplash URLs
  if (url.includes('unsplash.com')) {
    try {
      const optimizedUrl = new URL(url);
      optimizedUrl.searchParams.set('w', String(width));
      if (height) optimizedUrl.searchParams.set('h', String(height));
      optimizedUrl.searchParams.set('q', String(quality));
      optimizedUrl.searchParams.set('auto', 'format');
      optimizedUrl.searchParams.set('fm', format);
      optimizedUrl.searchParams.set('fit', fit === 'contain' ? 'max' : 'crop');
      return optimizedUrl.toString();
    } catch {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}w=${width}&q=${quality}&auto=format&fm=${format}&fit=crop`;
    }
  }

  // Handle Cloudflare / TableGreet Media CDN and Storage URLs
  if (url.includes('media.tablegreet.com') || url.includes('/storage/media/') || url.includes('/storage/v1/object/public/')) {
    try {
      const optimizedUrl = new URL(url, 'https://media.tablegreet.com');
      if (width) optimizedUrl.searchParams.set('w', String(width));
      if (height) optimizedUrl.searchParams.set('h', String(height));
      if (quality) optimizedUrl.searchParams.set('q', String(quality));
      if (fit) optimizedUrl.searchParams.set('fit', fit);
      if (format && format !== 'auto') optimizedUrl.searchParams.set('fm', format);
      return optimizedUrl.toString();
    } catch {
      return url;
    }
  }

  return url;
}

export function ensureHex(color: string | null | undefined, fallback = '#ff4f00'): string {
  if (!color) return fallback;
  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`;
  }
  if (/^#[0-9a-fA-F]{8}$/.test(trimmed)) {
    return trimmed.slice(0, 7);
  }

  const match = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)$/i);
  if (match) {
    const r = Math.min(255, Math.max(0, parseInt(match[1], 10))).toString(16).padStart(2, '0');
    const g = Math.min(255, Math.max(0, parseInt(match[2], 10))).toString(16).padStart(2, '0');
    const b = Math.min(255, Math.max(0, parseInt(match[3], 10))).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  return fallback;
}

// Resolves the local development host dynamically so QR codes and preview URLs work seamlessly when accessed from physical phones or tablets on the same Wi-Fi network.
function resolveDevHost(): string {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const configuredHost = env?.VITE_DEV_LAN_HOST?.trim();
  if (configuredHost) {
    return configuredHost;
  }
  if (typeof window !== 'undefined' && window.location.hostname) {
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1' && host !== '[::1]') {
      return host;
    }
  }
  return 'localhost';
}

export function getPlatformMainUrl(): string {
  if (import.meta.env.DEV) {
    const devHost = resolveDevHost();
    return `http://${devHost}:2000`;
  }
  return 'https://tablegreet.com';
}

export function getPlatformMenuUrl(): string {
  if (import.meta.env.DEV) {
    const devHost = resolveDevHost();
    return `http://${devHost}:2002`;
  }
  return 'https://venue.tablegreet.com';
}

export function getSignageEngineUrl(): string {
  if (import.meta.env.DEV) {
    const devHost = resolveDevHost();
    return `http://${devHost}:2007`;
  }
  return 'https://signage.tablegreet.com';
}

export function hexToRgb(hexColor: string | null | undefined, fallback = '255, 79, 0'): string {
  if (!hexColor) return fallback;
  const hex = ensureHex(hexColor).replace('#', '');
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `${r}, ${g}, ${b}`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }
  return fallback;
}
